import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createSlots as _createSlots, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps, createBlock as _createBlock, mergeProps as _mergeProps, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "space-y-4 p-4 sm:p-6 lg:p-8" }
const _hoisted_2 = { class: "cursor-pointer text-xl font-semibold text-gray-800" }
const _hoisted_3 = { class: "mt-3 flex items-center space-x-2" }
const _hoisted_4 = { class: "text-sm" }
const _hoisted_5 = { class: "font-semibold" }
const _hoisted_6 = {
  key: 0,
  class: "text-danger-700 rounded border border-red-300 bg-red-100 px-1.5 py-0.5 text-xs font-semibold"
}
const _hoisted_7 = { class: "flex gap-2" }

import { computed, onMounted, ref, WritableComputedRef } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';

import store from '@/store';
import { useUiStore } from '@/stores';
import { ConnectFlowService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import { formatDateAsString } from '@/utils/datetime';

import {
    ConnectFlowModelingGetters,
    ConnectFlowModelingActions,
    ConnectFlowServiceSaveFlowResult,
    ConnectFlowModel,
    EdgeType,
    Permission,
    NodeType,
    VueFlowNodeModel,
} from '@/types';

import {
    ComponentSelector,
    CustomNode,
    FormClienteInfoEdge,
    FormAwaitableEdge,
} from './components/flow';

import { AwaitableEdge, ClientInfoEdge } from '@/views/pages/connect-flow/components/flow/edges';
import { StartNode } from './components/flow/nodes';
import { VueFlow, useVueFlow } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls, ControlButton } from '@vue-flow/controls';
import { MiniMap } from '@vue-flow/minimap';

import { ArrowsExpandIcon } from '@heroicons/vue/outline';


export default /*@__PURE__*/_defineComponent({
  __name: 'ConnectFlowModelingPage',
  props: {
    action: {
        type: String,
        default: 'view',
    },
},
  setup(__props) {

const route = useRoute();
const uiStore = useUiStore();
const { t } = useI18n();

const props = __props;

const fullScreen = ref(false);
const showFormClienteInfoEdge = ref(false);
const showFormAwaitableEdge = ref(false);
const triggeredEdge = ref();

const connectFlow: WritableComputedRef<ConnectFlowModel> = computed({
    get: () => store.getters['flow/' + ConnectFlowModelingGetters.CONNECT_FLOW],
    set: (value) => store.dispatch('flow/' + ConnectFlowModelingActions.SET_CONNECT_FLOW, value),
});

const elements = computed({
    get: () => store.getters['flow/' + ConnectFlowModelingGetters.ELEMENTS],
    set: (value) => store.dispatch('flow/' + ConnectFlowModelingActions.SET_ELEMENTS, value),
});

const isAllElementsValid = computed(
    () => store.getters['flow/' + ConnectFlowModelingGetters.IS_ALL_ELEMENTS_VALID],
);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const save = async () => {
    try {
        await setLoading(true);
        const response = await ConnectFlowService.saveFlow<ConnectFlowServiceSaveFlowResult>(
            route.params.id.toString(),
            elements.value,
        );
        connectFlow.value = response.data.connectFlow;
        toast.success(response.data.message);
    } finally {
        await setLoading(false);
    }
};

const publish = async () => {
    try {
        await setLoading(true);

        const invalidVirtualAgents = (elements.value as VueFlowNodeModel[]).filter(
            (el) =>
                (el as VueFlowNodeModel).data?.component === NodeType.TRANSFER_VIRTUAL_AGENT &&
                !(el as VueFlowNodeModel).data?.virtualAgentNumber,
        );

        if (invalidVirtualAgents.length > 0) {
            toast.error(t('connectFlow.node.transferVirtualAgent.validation'));
            return;
        }

        await ConnectFlowService.saveFlow<ConnectFlowServiceSaveFlowResult>(
            route.params.id.toString(),
            elements.value,
        );

        const response = await ConnectFlowService.publishFlow<ConnectFlowServiceSaveFlowResult>(
            route.params.id.toString(),
            elements.value,
        );

        connectFlow.value = response.data.connectFlow;
        toast.success(response.data.message);
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const { onPaneReady, onEdgeClick } = useVueFlow();

onPaneReady(({ fitView }) => {
    fitView();
});

onEdgeClick((event) => {
    if (event.edge.data.component === EdgeType.AWAITABLE) {
        showFormAwaitableEdge.value = true;
        triggeredEdge.value = event.edge.id;
    }
    if (event.edge.data.component === EdgeType.CLIENT_INFO_EDGE) {
        showFormClienteInfoEdge.value = true;
        triggeredEdge.value = event.edge.id;
    }
});

onMounted(async () => {
    try {
        await setLoading(true);
        await store.dispatch('flow/' + ConnectFlowModelingActions.LOAD_FLOW_FLOW, {
            id: route.params.id,
            historyId: route.params.historyId,
        });
    } finally {
        await setLoading(false);
    }
});

return (_ctx: any,_cache: any) => {
  const _component_UiBackButton = _resolveComponent("UiBackButton")!
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiPageHeader = _resolveComponent("UiPageHeader")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_UiPageHeader, { class: "mb-5" }, _createSlots({
      info: _withCtx(() => [
        _createElementVNode("h1", _hoisted_2, _toDisplayString(_ctx.$t('connectFlow.' + props.action + '.title')), 1),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("p", _hoisted_4, [
            _createElementVNode("span", _hoisted_5, _toDisplayString(_unref(t)('connectFlow.labels.lastPublished')), 1),
            _createTextVNode(" " + _toDisplayString(_unref(formatDateAsString)(connectFlow.value?.updatedAt, 'LLL')), 1)
          ]),
          (
                            connectFlow.value !== undefined &&
                            connectFlow.value.currentFlowId !== connectFlow.value.lastAppliedFlowId
                        )
            ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(_unref(t)('connectFlow.labels.notPublished')), 1))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 2
    }, [
      (_ctx.$can(_unref(Permission).CREATE_CONNECT_FLOW))
        ? {
            name: "actions",
            fn: _withCtx(() => [
              _createElementVNode("div", _hoisted_7, [
                _createVNode(_component_UiBackButton, { fallbackTo: { name: 'ListConnectFlow' } }),
                _createVNode(_component_UiButton, {
                  variant: "primary",
                  "text-variant": "white",
                  onClick: save
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Save')), 1)
                  ]),
                  _: 1
                }),
                _createVNode(_component_UiButton, {
                  variant: "emerald",
                  "text-variant": "white",
                  disabled: !isAllElementsValid.value,
                  title: isAllElementsValid.value ? '' : _unref(t)('connectFlow.error.hasInvalidNodes'),
                  onClick: publish
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('core.actions.Publish')), 1)
                  ]),
                  _: 1
                }, 8, ["disabled", "title"])
              ])
            ]),
            key: "0"
          }
        : undefined
    ]), 1024),
    _createElementVNode("div", {
      style: _normalizeStyle(fullScreen.value ? '' : 'height: calc(100vh - 170px);'),
      class: _normalizeClass(["rounded bg-gray-200 shadow", fullScreen.value ? 'fixed right-0 top-0 z-40 h-full w-full' : 'h-5/6'])
    }, [
      _createVNode(_unref(VueFlow), {
        modelValue: elements.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((elements).value = $event)),
        "default-zoom": 0.5,
        "max-zoom": 1,
        "min-zoom": 0.45,
        "snap-grid": [5, 5],
        "snap-to-grid": "",
        "fit-view-on-init": ""
      }, {
        "node-input": _withCtx((node) => [
          _createVNode(_unref(StartNode), {
            nodeId: node.id
          }, null, 8, ["nodeId"])
        ]),
        "node-default": _withCtx((node) => [
          _createVNode(_unref(CustomNode), {
            nodeId: node.id
          }, null, 8, ["nodeId"])
        ]),
        "edge-custom": _withCtx((edge) => [
          ([_unref(EdgeType).AWAITABLE, _unref(EdgeType).NORMAL].includes(edge.data.component))
            ? (_openBlock(), _createBlock(_unref(AwaitableEdge), _normalizeProps(_mergeProps({ key: 0 }, edge)), null, 16))
            : _createCommentVNode("", true),
          (edge.data.component === _unref(EdgeType).CLIENT_INFO_EDGE)
            ? (_openBlock(), _createBlock(_unref(ClientInfoEdge), _normalizeProps(_mergeProps({ key: 1 }, edge)), null, 16))
            : _createCommentVNode("", true)
        ]),
        default: _withCtx(() => [
          _createVNode(_unref(Background)),
          _createVNode(_unref(MiniMap)),
          _createVNode(_unref(Controls), null, {
            default: _withCtx(() => [
              _createVNode(_unref(ControlButton), null, {
                default: _withCtx(() => [
                  _createVNode(_unref(ArrowsExpandIcon), {
                    class: "w-6",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (fullScreen.value = !fullScreen.value))
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"])
    ], 6),
    _createVNode(_unref(FormClienteInfoEdge), {
      modelValue: showFormClienteInfoEdge.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showFormClienteInfoEdge).value = $event)),
      edgeId: triggeredEdge.value
    }, null, 8, ["modelValue", "edgeId"]),
    _createVNode(_unref(FormAwaitableEdge), {
      modelValue: showFormAwaitableEdge.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((showFormAwaitableEdge).value = $event)),
      edgeId: triggeredEdge.value
    }, null, 8, ["modelValue", "edgeId"]),
    _createVNode(_unref(ComponentSelector))
  ]))
}
}

})